/**
 * <StatusPill> and <ExceptionBadge> — 14-theme.md §7, 13-ui-screens.md §3.4/§3.5.
 *
 * Not an antd colour preset: we need background + text + border independently,
 * and antd 6's `<Tag color>` sets only one of the three. The `style` prop wins
 * over tokens, so the ten triples below are exact.
 *
 * antd 6: `Tag` lost its trailing default margin and NO token restores it.
 * Lay pills out with `<Space size={8}>` or a flex `gap: 8px`; `margin: 0` is
 * set explicitly here so nothing depends on the component's own spacing.
 * `bordered` was replaced by `variant` in v6 — we pass neither and paint the
 * border ourselves.
 */
import { Tag } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import type { ExceptionSeverity } from '@/api/types';

export type PillKind =
  | 'open'
  | 'closed'
  | 'pending'
  | 'approved'
  | 'active'
  | 'locked'
  | 'expired'
  | 'valid'
  | 'soon'
  | 'noEntry'
  | 'superseded';

const PILL: Record<PillKind, { bg: string; fg: string; bd: string }> = {
  open: { bg: 'var(--green-100)', fg: 'var(--green-800)', bd: 'var(--green-300)' },
  approved: { bg: 'var(--green-100)', fg: 'var(--green-800)', bd: 'var(--green-300)' },
  valid: { bg: 'var(--green-100)', fg: 'var(--green-800)', bd: 'var(--green-300)' },
  closed: { bg: 'var(--neutral-100)', fg: 'var(--text-body)', bd: 'var(--border-default)' },
  pending: { bg: 'var(--cm-amber-bg)', fg: 'var(--cm-amber-text)', bd: 'var(--cm-amber-border)' },
  soon: { bg: 'var(--cm-amber-bg)', fg: 'var(--cm-amber-text)', bd: 'var(--cm-amber-border)' },
  noEntry: { bg: 'var(--cm-amber-bg)', fg: 'var(--cm-amber-text)', bd: 'var(--cm-amber-border)' },
  active: { bg: 'var(--surface-subtle)', fg: 'var(--neutral-600)', bd: 'var(--border-subtle)' },
  superseded: { bg: 'var(--surface-subtle)', fg: 'var(--neutral-600)', bd: 'var(--border-subtle)' },
  /* Locked is the ONLY filled-dark pill in the entire system. Nothing else may
   * look like it — that is the visual guarantee of FR-6.7 immutability. */
  locked: { bg: 'var(--brand-deep)', fg: 'var(--white)', bd: 'var(--brand-deep)' },
  expired: { bg: 'var(--cm-danger-bg)', fg: 'var(--cm-danger-text)', bd: 'var(--cm-danger-border)' },
};

const PILL_SHAPE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  margin: 0,
  borderRadius: 3,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: '18px',
  borderWidth: 1,
  borderStyle: 'solid',
  whiteSpace: 'nowrap',
};

export interface StatusPillProps {
  kind: PillKind;
  children: ReactNode;
  title?: string;
}

export function StatusPill({ kind, children, title }: StatusPillProps) {
  const c = PILL[kind];
  return (
    <Tag style={{ ...PILL_SHAPE, background: c.bg, color: c.fg, borderColor: c.bd }} title={title}>
      {children}
    </Tag>
  );
}

/* ─────────────────────────── exception badges ─────────────────────────── */

const BADGE: Record<ExceptionSeverity | 'guest', { bg: string; fg: string; bd: string }> = {
  danger: { bg: 'var(--cm-danger-bg)', fg: 'var(--cm-danger-text)', bd: 'var(--cm-danger-border)' },
  warning: { bg: 'var(--cm-amber-bg)', fg: 'var(--cm-amber-text)', bd: 'var(--cm-amber-border)' },
  neutral: { bg: 'var(--neutral-100)', fg: 'var(--neutral-600)', bd: 'var(--border-default)' },
  /* Borrowed from another team — the earth band, `FROM I1`. */
  guest: { bg: 'var(--earth-100)', fg: 'var(--earth-700)', bd: 'var(--earth-300)' },
};

const BADGE_SHAPE: CSSProperties = {
  ...PILL_SHAPE,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.06em',
  lineHeight: '16px',
  textTransform: 'uppercase',
};

export interface ExceptionBadgeProps {
  /** The config row's `code` — `MC`, `AL`, `ABS`… */
  code: string;
  /** The word shown. Always a word: colour is never the only signal. */
  label: string;
  /** `exception_code.severity`. Whether the code reduces the divisor is a
   *  SEPARATE flag (OQ-8) and does not change the badge's appearance. */
  severity: ExceptionSeverity;
}

export function ExceptionBadge({ code, label, severity }: ExceptionBadgeProps) {
  const c = BADGE[severity];
  return (
    <Tag style={{ ...BADGE_SHAPE, background: c.bg, color: c.fg, borderColor: c.bd }} title={code}>
      {label}
    </Tag>
  );
}

/** `FROM I1` — a worker borrowed from another team. */
export function GuestBadge({ homeTeamCode }: { homeTeamCode: string }) {
  const c = BADGE.guest;
  return (
    <Tag style={{ ...BADGE_SHAPE, background: c.bg, color: c.fg, borderColor: c.bd }}>
      {`FROM ${homeTeamCode}`}
    </Tag>
  );
}

/* ───────────────────────── variance delta chip ────────────────────────── */

export interface DeltaChipProps {
  /** Already-formatted delta text, e.g. `+10.9%` or `(RM 12.40)`. */
  children: ReactNode;
  /**
   * `VarianceRowDto.isOutlier` + the sign. The ±15 % threshold is
   * CONFIGURATION (`VarianceQuery.thresholdPercent`) and the server decides —
   * this chip must never re-apply a hardcoded threshold.
   */
  tone: 'neutral' | 'up' | 'down';
}

export function DeltaChip({ children, tone }: DeltaChipProps) {
  const cls = tone === 'neutral' ? 'cm-delta' : `cm-delta cm-delta--${tone}`;
  return <span className={cls}>{children}</span>;
}
